import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react'
import classNames from 'classnames'

type TabsValue = string

type TabsContextValue = {
  baseId: string
  value?: TabsValue
  setValue: (nextValue: TabsValue) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

const useTabsContext = (): TabsContextValue => {
  const ctx = useContext(TabsContext)
  if (!ctx) {
    throw new Error('Tabs components must be used within <Tabs>.')
  }
  return ctx
}

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: TabsValue
  defaultValue?: TabsValue
  onValueChange?: (value: TabsValue) => void
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {value: controlledValue, defaultValue, onValueChange, className, ...props},
    ref,
  ) => {
    const reactId = useId()
    const baseId = `tabs-${reactId}`

    const [uncontrolledValue, setUncontrolledValue] = useState<TabsValue>(
      defaultValue ?? '',
    )

    const value = controlledValue ?? uncontrolledValue

    const setValue = useCallback(
      (nextValue: TabsValue) => {
        if (controlledValue === undefined) setUncontrolledValue(nextValue)
        onValueChange?.(nextValue)
      },
      [controlledValue, onValueChange],
    )

    const ctx = useMemo<TabsContextValue>(
      () => ({
        baseId,
        value,
        setValue,
      }),
      [baseId, setValue, value],
    )

    return (
      <TabsContext.Provider value={ctx}>
        <div
          ref={ref}
          className={classNames('flex flex-col', className)}
          {...props}
        />
      </TabsContext.Provider>
    )
  },
)
Tabs.displayName = 'Tabs'

export type TabsListProps = React.HTMLAttributes<HTMLDivElement>

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({className, ...props}, ref) => {
    return (
      <div
        ref={ref}
        role="tablist"
        className={classNames('flex flex-row gap-2 karla-bold', className)}
        {...props}
      />
    )
  },
)
TabsList.displayName = 'TabsList'

export interface TabsTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: TabsValue
}

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({value, className, disabled, onClick, type = 'button', ...props}, ref) => {
    const {baseId, setValue, value: activeValue} = useTabsContext()
    const isActive = activeValue === value

    const triggerId = `${baseId}-trigger-${value}`
    const contentId = `${baseId}-content-${value}`

    return (
      <button
        ref={ref}
        type={type}
        role="tab"
        id={triggerId}
        aria-selected={isActive}
        aria-controls={contentId}
        disabled={disabled}
        data-state={isActive ? 'active' : 'inactive'}
        className={classNames(
          'cursor-pointer border-b-2 border-transparent',
          isActive
            ? 'text-white border-white'
            : 'text-gray-1000 hover:text-gray-300',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
        onClick={e => {
          if (disabled) return
          setValue(value)
          onClick?.(e)
        }}
        {...props}
      />
    )
  },
)
TabsTrigger.displayName = 'TabsTrigger'

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: TabsValue
  forceMount?: boolean
}

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(
  ({value, forceMount = false, className, ...props}, ref) => {
    const {baseId, value: activeValue} = useTabsContext()
    const isActive = activeValue === value

    const triggerId = `${baseId}-trigger-${value}`
    const contentId = `${baseId}-content-${value}`

    if (!forceMount && !isActive) return null

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={contentId}
        aria-labelledby={triggerId}
        data-state={isActive ? 'active' : 'inactive'}
        hidden={!isActive}
        className={classNames(className)}
        {...props}
      />
    )
  },
)
TabsContent.displayName = 'TabsContent'
